import React, { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import utils from '../utils';
import tenantUtils from '../tenantUtils';
import '../styles/main.scss'
import SupportModal from '../components/SupportModal';
import FirstTimeUserModal from '../components/FirstTimeUserModal';
import SignedOutPage from './SignedOutPage';
import PrtLandPage from '../components/PrtLandPage';
import ListSkeletonLoader from '../components/Layout/ListSkeletonLoader';
import { datadogRum } from '@datadog/browser-rum';
import FloatingFeedbackButton from '../components/Layout/FloatingFeedbackButton';
import ErrorPage from './ErrorPage';
import ToastDynamic from '../components/Layout/ToastDynamic/ToastDynamic';
import ConsentModal from '../components/ConsentModal';


function SignedInPage({special=null}) {

    const { user, getAccessTokenSilently, getAccessTokenWithPopup, loginWithRedirect, isAuthenticated, isLoading, logout } = useAuth0();
    const accounts = user ? [user] : [];

    const { search } = useLocation();

    let [accessToken, setAccessToken] = useState(null);
    let [tenantSettings, setTenantSettings] = useState(null);
    let [firstTimeUser, setFirstTimeUser] = useState(false);
    let [pendingApproval, setPendingApproval] = useState(false);
    let [loading, setLoading] = useState(true);
    let [curNav, setCurNav] = useState();
    let [notifications, setNotifications] = useState();
    let [supportModalShown, setSupportModalShown] = useState(false);
    let [tooltip, setTooltip] = useState([]);
    let [apiError, setApiError] = useState();
    let [queryRequest, setQueryRequest] = useState(null);
    let [showNav, setShowNav] = useState(false);
    let [windowSize, setWindowSize] = useState(window.innerWidth);
    let [supportContext] = useState();
    let [userAppsList, setUserAppsList] = useState(null);
    let [showToast, setShowToast] = useState(false);
    let [toastType, setToastType] = useState(null);
    let [consentRequired, setConsentRequired] = useState(false);
    let [consentVersion, setConsentVersion] = useState(null);

    const history = useHistory();
    const { path } = useRouteMatch();

    const request = {
        authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: 'read:current_user user:read openid profile',
        }
    };

    const toastContent = toastType === 'success' ? "Feedback submitted successfully. Thank you!" : "Error submitting feedback. Please try again."

    const setSupportModalIsShown = useCallback((newVal) => {
        setSupportModalShown(!!newVal);
    }, []);

    const getNewScreenWidth = (screenSize) => {
        setWindowSize(screenSize);
    }

    const handleCloseToast = () => {
        setShowToast(false);
        setToastType(null);
    }

    const showToastHandler = useCallback((type) => {
    setToastType(type);
    setShowToast(true);
    setSupportModalShown(false);
    }, []);

    const getAccessToken = async () => {
        try {
            const theAccToken = await getAccessTokenSilently(request);
            const tokenObj = { accessToken: theAccToken };
            setAccessToken(tokenObj);
            identifyUser(user?.sub, user?.name, user?.email);
            return tokenObj;
        } catch (error) {
            console.error('Error getting access token:', error);
            // A Post-Login Action requires user interaction (e.g. profile form on first login).
            // Silent auth (prompt=none) cannot show UI — redirect to full interactive login.
            if (error.error === 'interaction_required' || error.error === 'login_required') {
                loginWithRedirect({ appState: { returnTo: window.location.pathname } });
                return null;
            }
            try {
                const theAccToken = await getAccessTokenWithPopup(request);
                const tokenObj = { accessToken: theAccToken };
                setAccessToken(tokenObj);
                return tokenObj;
            } catch (finalError) {
                console.error('Final auth attempt failed:', finalError);
                return null;
            }
        }
    }

    //Filters list of notification to only show notifications not stored in localStorage
    const filterLocalStorageNotes = (notes, userSettings) => {
        let tempArr = {
            data: []
        }

        if (notes && notes.data && notes.data.length > 0) {
            notes.data.forEach((notification) => {
                //checks to see if notification and userID exists in local storage.
                let localStrNote = localStorage.getItem(`${notification.contentID}${userSettings.ourUserID}`);
                //if not it is added to temp array and added to local storage
                if (!localStrNote) {
                    tempArr.data.push(notification);
                    localStorage.setItem(`${notification.contentID}${userSettings.ourUserID}`, notification.contentID)
                }
            })
            setNotifications(tempArr)
        } else {
            setNotifications(notes)
        }
    }

    //Gets a list of notifications to alert the user with
    const getNotifications = async (token, wkspID, userSettings) => {

        if (!token) {
            token = await getAccessToken()
        }
        let response = await fetch(`${process.env.REACT_APP_WEB_API_B2C}/api/workspace/${wkspID}/notifications`, {
            headers: {
                "Authorization": "Bearer " + token.accessToken
            }
        });
        let wkspNotifications = await response.json();
        //sends list of notifications to filter to only show ones user has not seen.
        filterLocalStorageNotes(wkspNotifications, userSettings);
    }

    const getTooltip = async () => {
        let response = await fetch(`${process.env.REACT_APP_WEB_API_B2C}/api/cms-content?contenttype=tooltips`, {});
        let jsonresponse = await response.json();
        setTooltip(jsonresponse.items);
    }
    
    const onQuery = (query) => {
        setQueryRequest(query);
        history.push(`${path}search/${encodeURIComponent(query)}?pg=home`);
    }

    const identifyUser = (userId, userName, userEmail) => {
        datadogRum.setUser({
          id: userId,
          name: userName,
          email: userEmail,
        });
      }

    const loadPageDetail = async () => {
    let curAccess;
    let tid = utils.getForceTenantValue();

    const urlParams = new URLSearchParams(search);

    if (urlParams.has('tenid') && urlParams.get('tenid').length > 0) {
        utils.setForceTenantValue(urlParams.get('tenid'));
        tid = utils.getForceTenantValue();
    }

    if (!accessToken) {
        curAccess = await getAccessToken();
    }

    // Check consent status — non-blocking on error so app still loads
    try {
        const token = curAccess?.accessToken || accessToken?.accessToken;
        const consentResp = await fetch(`${process.env.REACT_APP_WEB_API_B2C}/api/consent`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (consentResp.ok) {
            const consentStatus = await consentResp.json();
            if (consentStatus.requires_consent) {
                setConsentRequired(true);
                setConsentVersion(consentStatus.current_version);
            }
        }
    } catch (err) {
        // consent check failure doesn't block app load
    }

    if(!tooltip) {
        getTooltip();
    }

    const settings = await tenantUtils.getTenantInfo(curAccess, tid, special)

    if(settings) {
        if (settings.state === 'PENDING_APPROVAL') {
            setFirstTimeUser(true);
            setPendingApproval(true);
        } else if(settings.tenant && settings.tenant !== null && settings.firstTime !== true) {
            utils.setTrackingForWorkspace(settings && settings.tenant && settings.tenant.fields ? settings.tenant.fields.tenantslug : '');
            if (settings.firstLoginEvent) {
                datadogRum.addAction('first_login', { user_type: settings.firstLoginEvent });
            }
            getNotifications(curAccess, tid, settings)
        } else {
            setFirstTimeUser(true);
        }

        setTenantSettings(settings);

    } else {
        setApiError('Error #456078: Please refresh your browser or try signing up with a different account.');
    }
}


    useEffect(() => {
        let isMounted = true; // Flag to track if component is mounted
        
        const initializePage = async () => {
            // Only proceed if Auth0 is not loading and user is authenticated
            if (isAuthenticated && !isLoading && isMounted) {
                setLoading(true);
                try {
                    await loadPageDetail();
                } catch (error) {
                    console.error('Error during page initialization:', error);
                    if (isMounted) {
                        setApiError('Error loading page data. Please refresh your browser. If the problem persists, please contact support.');
                    }
                } finally {
                    if (isMounted) {
                        setLoading(false);
                    }
                }
            }
        };
        
        initializePage();
        
        // Cleanup function to prevent state updates on unmounted component
        return () => {
            isMounted = false;
        };
        
        // eslint-disable-next-line
    }, [isAuthenticated, isLoading]);

    useEffect(() => {
        const fetchUserApps = async () => {
            const tenID = tenantSettings?.tenant?.fields?.aDGroupID;
            const userID = user?.sub;
            if (!tenID || !userID || !accessToken?.accessToken) return;
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_WEB_API_B2C}/api/users/${userID}?tenid=${tenID}`,
                    { headers: { Authorization: 'Bearer ' + accessToken.accessToken } }
                );
                if (response.ok) {
                    const data = await response.json();
                    const workspace = data.userInfo?.wrkspaces?.find(w => w.RowKey === tenID);
                    const appsList = workspace?.appsList;
                    if (appsList != null) {
                        setUserAppsList(appsList ? appsList.split(',') : []);
                    }
                }
            } catch {
                // leave null — components handle null as loading/empty state
            }
        };
        fetchUserApps();
    }, [tenantSettings?.tenant?.fields?.aDGroupID, user?.sub, accessToken]);



    const showNavHandler = (forceValue = null) => {
        if (forceValue === null) {
            setShowNav(prev => !prev);
        } else {
            setShowNav(forceValue);
        }
    };
    

    const onLinkClick = (navLink) => {
        setCurNav(navLink);
    };

    // Needed for Workspaces with the Alchemy app
    // Forces token reset so user can sign out and is dirrected to the sign in page. 
    const resetToken = () => {
        setAccessToken(null);
    };

    if (pendingApproval) {
        return <SignedOutPage userOnboard={{ userInfo: { state: 'PENDING_APPROVAL' } }} />;
    }

    return (
        loading === true && windowSize ?
            <ListSkeletonLoader classes={'m-i-08 m-b-08'} preview={false} />
            :
            tenantSettings && isAuthenticated === true ?
                <div className={tenantSettings && tenantSettings.tenant && tenantSettings.tenant.fields ? tenantSettings.tenant.fields.tenantslug : ''}>

                    <PrtLandPage
                        tenantSettings={tenantSettings}
                        accounts={accounts}
                        notifications={notifications}
                        apiError={apiError}
                        tooltip={tooltip}
                        showNav={showNav}
                        setShowNav={setShowNav}
                        windowSize={windowSize}
                        curNav={curNav}
                        firstTimeUser={firstTimeUser}
                        accessToken={accessToken}
                        isAuthenticated={isAuthenticated}
                        queryRequest={queryRequest}
                        supportModalShown={supportModalShown}
                        onQuery={onQuery}
                        setSupportModalIsShown={setSupportModalIsShown}
                        getNewScreenWidth={getNewScreenWidth}
                        resetToken={resetToken}
                        showNavHandler={showNavHandler}
                        onLinkClick={onLinkClick}
                        userAppsList={userAppsList}
                    />
                    <FloatingFeedbackButton
                        isOpen={supportModalShown}
                        onClick={() => setSupportModalIsShown(true)}
                    />
                    <SupportModal
                        userEmail={user?.email || ''}
                        workspaceId={tenantSettings?.tenant?.fields?.aDGroupID || ''}
                        show={supportModalShown}
                        onSubmitted={setSupportModalIsShown}
                        contextInfo={supportContext}
                        formSubmitted={showToastHandler}
                    />
                    {
                        consentRequired &&
                            <ConsentModal
                                accessToken={accessToken?.accessToken}
                                version={consentVersion}
                                onConsent={() => setConsentRequired(false)}
                                onDecline={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                            />
                    }
                    {
                        firstTimeUser && firstTimeUser === true ?
                            <FirstTimeUserModal
                                userID={tenantSettings && tenantSettings.ourUserID ? tenantSettings.ourUserID : ''}
                                pendingApproval={pendingApproval}
                            />
                            : null
                    }
                    {
                        showToast ?
                            <ToastDynamic 
                                content={toastContent} 
                                show={showToast} 
                                handleCloseToast={() => handleCloseToast()} 
                                toastClasses={{}}
                                variant={toastType}
                            />
                        : null
                    }
                </div>
                : 
                <ErrorPage details={apiError} />
    )
}

export default SignedInPage